/**
 * App-wide Analytics Events
 *
 * Events for H3 features (notices, solutions, journals, voice, operating feed, wizards, action cards)
 * and HRI (HR Intelligence/HR Guidance) features.
 *
 * These events are shared across both app and nuc for consistency.
 * Module-specific events should be defined in their respective module's constants/events.ts file.
 */

export enum AppEvents {
  // Plan Upgrade Modal Events
  PLAN_UPGRADE_MODAL_SHOWN = 'plan-upgrade-modal-shown',
  PLAN_UPGRADE_MODAL_CLOSED = 'plan-upgrade-modal-closed',
  PLAN_UPGRADE_TAB_SWITCHED = 'plan-upgrade-tab-switched',
  PLAN_UPGRADE_BUTTON_CLICKED = 'plan-upgrade-button-clicked',
  PLAN_UPGRADE_REQUEST_GATE_SHOWN = 'plan-upgrade-request-gate-shown',
  PLAN_UPGRADE_REQUEST_GATE_CLOSED = 'plan-upgrade-request-gate-closed',

  // Premium People migration upgrade funnel (BIO-1185)
  PREMIUM_PEOPLE_LANDING_VIEWED = 'premium-people-landing-viewed',
  PREMIUM_PEOPLE_CONFIRM_VIEWED = 'premium-people-confirm-viewed',
  PREMIUM_PEOPLE_BACK_TO_LANDING = 'premium-people-back-to-landing',
  PREMIUM_PEOPLE_ACTIVATE_CLICKED = 'premium-people-activate-clicked',
  PREMIUM_PEOPLE_PAYMENT_REQUIRED = 'premium-people-payment-required',
  PREMIUM_PEOPLE_CARD_SUBMITTED = 'premium-people-card-submitted',
  PREMIUM_PEOPLE_SUBSCRIPTION_SUCCEEDED = 'premium-people-subscription-succeeded',
  PREMIUM_PEOPLE_SUBSCRIPTION_FAILED = 'premium-people-subscription-failed',
  PREMIUM_PEOPLE_SUCCESS_VIEWED = 'premium-people-success-viewed',
  PREMIUM_PEOPLE_FOUNDER_INSTALL_CLICKED = 'premium-people-founder-install-clicked',

  // $1 Premium People trial funnel (BIO-1350, /trial/premium-people). Names match
  // the Trial Premium People Event Spec. NOT here on purpose:
  //   - the 3 Stripe-webhook events (converted / canceled / charge-failed) — billing owns those;
  //   - the handoff + employee-count events — those fire in Hexagon (platform-mvp analytics-events.ts).
  TRIAL_PREMIUM_PEOPLE_LANDING_VIEWED = 'trial-premium-people-landing-viewed',
  TRIAL_PREMIUM_PEOPLE_VIDEO_PLAYED = 'trial-premium-people-video-played',
  TRIAL_PREMIUM_PEOPLE_TIMER_COMPLETE = 'trial-premium-people-timer-complete',
  TRIAL_PREMIUM_PEOPLE_START_CLICKED = 'trial-premium-people-start-clicked',
  TRIAL_PREMIUM_PEOPLE_CHECKOUT_VIEWED = 'trial-premium-people-checkout-viewed',
  TRIAL_PREMIUM_PEOPLE_REASON_SELECTED = 'trial-premium-people-reason-selected',
  TRIAL_PREMIUM_PEOPLE_TERMS_OPENED = 'trial-premium-people-terms-opened',
  TRIAL_PREMIUM_PEOPLE_PAYMENT_SUBMITTED = 'trial-premium-people-payment-submitted',
  TRIAL_PREMIUM_PEOPLE_STARTED = 'trial-premium-people-started',
  // Fired client-side the moment plan/convert returns success (the $1 clears).
  // NOTE: the spec's `converted` is the Stripe-webhook "first REAL monthly charge"
  // event (~14 days later). This reuses that name for the $1 conversion at the
  // product owner's request — see the funnel spec's caution about name reuse.
  TRIAL_PREMIUM_PEOPLE_CONVERTED = 'trial-premium-people-converted',
  TRIAL_PREMIUM_PEOPLE_PAYMENT_FAILED = 'trial-premium-people-payment-failed',

  // Migration flow funnel (BIO-1940, /migration). One event per step so the Hex
  // funnel (BIO-1941) reads as a single scan. Every event carries the shared
  // property set built by useMigrationFlow's `trackStep`.
  MIGRATION_INTRO_VIEWED = 'migration-intro-viewed',
  MIGRATION_WHATS_NEW_VIEWED = 'migration-whats-new-viewed',
  MIGRATION_COMPANY_PLAN_VIEWED = 'migration-company-plan-viewed',
  MIGRATION_LEVEL_VIEWED = 'migration-level-viewed',
  MIGRATION_LEVEL_SELECTED = 'migration-level-selected',
  MIGRATION_PLANS_VIEWED = 'migration-plans-viewed',
  MIGRATION_PLAN_SELECTED = 'migration-plan-selected',
  MIGRATION_PLAN_DETAILS_OPENED = 'migration-plan-details-opened',
  MIGRATION_OFFER_VIEWED = 'migration-offer-viewed',
  MIGRATION_PLAN_CONFIRMED = 'migration-plan-confirmed',
  MIGRATION_ADVISOR_REQUESTED = 'migration-advisor-requested',

  // Info Modal Events
  SOLUTIONS_INFO_MODAL_SHOWN = 'solutions-info-modal-shown',
  SOLUTIONS_INFO_MODAL_CLOSED = 'solutions-info-modal-closed',
  OPERATING_FEED_INFO_MODAL_SHOWN = 'operating-feed-info-modal-shown',
  OPERATING_FEED_INFO_MODAL_CLOSED = 'operating-feed-info-modal-closed',

  // Notice Events
  NOTICE_VIEWED = 'notice_viewed',
  NOTICE_DISMISSED = 'notice_dismissed',
  NOTICE_RESTORED = 'notice_restored',
  NOTICE_ASSIGNED_TO_BAMBEE = 'notice_assigned_to_bambee',
  NOTICE_DISCUSS_WITH_HR_CLICKED = 'notice_discuss_with_hr_clicked',
  NOTICE_RATED = 'notice_rated',
  NOTICE_LINK_CLICKED = 'notice_link_clicked',

  // Notice Solution Events
  NOTICE_SOLUTION_CLICKED = 'notice_solution_clicked',
  NOTICE_SOLUTION_MARKED_DONE = 'notice_solution_marked_done',
  NOTICE_SOLUTION_DETAILS_VIEWED = 'notice_solution_details_viewed',

  // Solution Events
  SOLUTION_CARD_NOTICE_BADGE_CLICKED = 'solution_card_notice_badge_clicked',
  SOLUTION_CARD_DESCRIPTION_TOGGLED = 'solution_card_description_toggled',
  SOLUTION_ASSIGNED_TO_BAMBEE = 'solution_assigned_to_bambee',
  SOLUTION_DO_IT_MYSELF_CLICKED = 'solution_do_it_myself_clicked',
  SOLUTION_MARK_AS_DONE = 'solution_mark_as_done',
  SOLUTION_COMPLETED = 'solution_completed',
  SOLUTION_DETAILS_VIEWED = 'solution_details_viewed',
  SOLUTIONS_DASHBOARD_VIEWED = 'solutions_dashboard_viewed',
  SOLUTIONS_LEARN_MORE_CLICKED = 'solutions_learn_more_clicked',

  // Journal Events
  JOURNAL_LIST_VIEWED = 'journal_list_viewed',
  JOURNAL_ENTRY_VIEWED = 'journal_entry_viewed',
  JOURNAL_ENTRY_CREATED = 'journal_entry_created',
  JOURNAL_ENTRY_COMPLETED = 'journal_entry_completed',
  JOURNAL_ENTRY_DISMISSED = 'journal_entry_dismissed',
  JOURNAL_ENTRY_DELETED = 'journal_entry_deleted',
  JOURNAL_SEARCH_USED = 'journal_search_used',
  JOURNAL_FILTER_APPLIED = 'journal_filter_applied',
  JOURNAL_TAB_CHANGED = 'journal_tab_changed',

  // Journal Voice Events
  JOURNAL_VOICE_ACTIVATED = 'journal_voice_activated',
  JOURNAL_VOICE_DEACTIVATED = 'journal_voice_deactivated',
  JOURNAL_VOICE_ERROR = 'journal_voice_error',

  // Voice Session Events
  VOICE_SESSION_STARTED = 'voice_session_started',
  VOICE_SESSION_ENDED = 'voice_session_ended',
  VOICE_SESSION_ERROR = 'voice_session_error',
  VOICE_RECONNECTED = 'voice_reconnected',

  // Voice History Events
  VOICE_HISTORY_NEW_CHAT_CLICKED = 'voice_history_new_chat_clicked',
  VOICE_HISTORY_THREAD_OPENED = 'voice_history_thread_opened',

  // Operating Feed Events
  OPERATING_FEED_VIEWED = 'operating_feed_viewed',
  OPERATING_FEED_FILTER_APPLIED = 'operating_feed_filter_applied',
  OPERATING_FEED_SEARCH_USED = 'operating_feed_search_used',
  OPERATING_FEED_LOAD_MORE = 'operating_feed_load_more',
  OPERATING_FEED_REFRESHED = 'operating_feed_refreshed',
  OPERATING_FEED_BACKGROUND_REFRESH = 'operating_feed_background_refresh',
  OPERATING_FEED_LEARN_MORE_CLICKED = 'operating_feed_learn_more_clicked',

  // HR Call Scheduling Events
  HR_CALL_SCHEDULED = 'hr_call_scheduled',
  HR_CALL_SCHEDULING_ERROR = 'hr_call_scheduling_error',

  // Account Activation Events
  ACCOUNT_ACTIVATION_SUBMITTED = 'account_activation_submitted',
  ACCOUNT_ACTIVATION_SUCCESS = 'account_activation_success',
  ACCOUNT_ACTIVATION_FAILED = 'account_activation_failed',
  ACCOUNT_ACTIVATION_VALIDATION_ERROR = 'account_activation_validation_error',

  // Action Card Events
  ACTION_CARD_SHOWN = 'action_card_shown',
  ACTION_CARD_DISMISSED = 'action_card_dismissed',

  // Wizard Events
  WIZARD_STARTED = 'wizard_started',
  WIZARD_COMPLETED = 'wizard_completed',
  WIZARD_EXITED = 'wizard_exited',
  WIZARD_STEP_VIEWED = 'wizard_step_viewed',
  WIZARD_STEP_COMPLETED = 'wizard_step_completed',
  WIZARD_STEP_BACK = 'wizard_step_back',
  WIZARD_VALIDATION_FAILED = 'wizard_validation_failed',
  WIZARD_FIELD_UPDATED = 'wizard_field_updated',
  WIZARD_PROGRESS_SAVED = 'wizard_progress_saved',
  WIZARD_VOICE_DATA_UPDATED = 'wizard_voice_data_updated',
  WIZARD_VOICE_NAVIGATION = 'wizard_voice_navigation',

  // Permission & Upgrade Events
  PERMISSION_CHECK_FAILED = 'permission_check_failed',
  UPGRADE_TRIGGERED = 'upgrade_triggered',

  // Plan Upgrade Gate Events
  PLAN_UPGRADE_GATE_SHOWN = 'plan_upgrade_gate_shown',
  PLAN_UPGRADE_GATE_DISMISSED = 'plan_upgrade_gate_dismissed',
  PLAN_UPGRADE_GATE_BUTTON_CLICKED = 'plan_upgrade_gate_button_clicked',
  PLAN_UPGRADE_GATE_LEARN_MORE_CLICKED = 'plan_upgrade_gate_learn_more_clicked',

  // HR Guidance Events (HRI)
  HR_GUIDANCE_ANNUAL_PLAN_REQUESTED = 'hr-guidance-annual-plan-requested',
  HR_GUIDANCE_START = 'hr-guidance-start',
  HR_GUIDANCE_FINALIZE = 'hr-guidance-finalize',
  HR_GUIDANCE_DELEGATE_TASK_CLICKED = 'hr-guidance-delegate-task-clicked',
  HR_GUIDANCE_DELEGATE_TASK_SUCCESS = 'hr-guidance-delegate-task-success',
  HR_GUIDANCE_DELEGATE_TASK_ERROR = 'hr-guidance-delegate-task-error',
  HR_GUIDANCE_SELF_SERVE_PLAN_UPGRADE_MODAL_SHOWN = 'self-serve-plan-upgrade-modal-shown',
  HR_GUIDANCE_SELF_SERVE_PLAN_UPGRADE_SUCCESS = 'self-serve-plan-upgrade-success',
  HR_GUIDANCE_SELF_SERVE_PLAN_UPGRADE_ERROR = 'self-serve-plan-upgrade-error',
  HR_GUIDANCE_SCHEDULE_CALL_CLICKED = 'hr-guidance-schedule-call-clicked',
  HR_GUIDANCE_BLACKLISTED_OPTION_SELECTED = 'hr-guidance-blacklisted-option-selected',
  HR_GUIDANCE_HANDLE_THIS_FOR_ME_PAYWALL_SHOWN = 'hr-guidance-handle-this-for-me-paywall-shown',
  HR_GUIDANCE_UNLOCK_ACTION_PLAN_CLICKED = 'hr-guidance-unlock-action-plan-clicked',

  // HR Guidance Info Modal Events (HRI)
  HR_GUIDANCE_INFO_MODAL_OPEN = 'APP_HR_GUIDANCE_INFO_MODAL_OPEN',
  HR_GUIDANCE_INFO_MODAL_EXIT = 'APP_HR_GUIDANCE_INFO_MODAL_EXIT',
  HR_GUIDANCE_INFO_MODAL_RE_OPEN = 'APP_HR_GUIDANCE_INFO_MODAL_RE_OPEN',
  HR_GUIDANCE_INFO_MODAL_GO_TO_SECOND_PANEL = 'APP_HR_GUIDANCE_INFO_MODAL_GO_TO_SECOND_PANEL',
  HR_GUIDANCE_INFO_MODAL_GO_TO_THIRD_PANEL = 'APP_HR_GUIDANCE_INFO_MODAL_GO_TO_THIRD_PANEL',
  HR_GUIDANCE_INFO_MODAL_GO_TO_FOURTH_PANEL = 'APP_HR_GUIDANCE_INFO_MODAL_GO_TO_FOURTH_PANEL',
  HR_GUIDANCE_INFO_MODAL_FINISH = 'APP_HR_GUIDANCE_INFO_MODAL_FINISH',
  HR_GUIDANCE_INFO_MODAL_LEARN_MORE = 'APP_HR_GUIDANCE_INFO_MODAL_LEARN_MORE',
  HR_GUIDANCE_DETAILS_INFO_MODAL_OPEN = 'APP_HR_GUIDANCE_DETAILS_INFO_MODAL_OPEN',
  HR_GUIDANCE_DETAILS_INFO_MODAL_EXIT = 'APP_HR_GUIDANCE_DETAILS_INFO_MODAL_EXIT',
  HR_GUIDANCE_DETAILS_INFO_MODAL_RE_OPEN = 'APP_HR_GUIDANCE_DETAILS_INFO_MODAL_RE_OPEN',
  HR_GUIDANCE_DETAILS_INFO_MODAL_FINISH = 'APP_HR_GUIDANCE_DETAILS_INFO_MODAL_FINISH',
  HR_GUIDANCE_DELEGATE_TASK_UPSELL_MODAL_OPEN = 'APP_HR_GUIDANCE_DELEGATE_TASK_UPSELL_MODAL_OPEN',
  HR_GUIDANCE_DELEGATE_TASK_UPSELL_MODAL_EXIT = 'APP_HR_GUIDANCE_DELEGATE_TASK_UPSELL_MODAL_EXIT',

  // Staff Role Events
  STAFF_ROLE_UPDATE_SUCCESS = 'staff_role_update_success',
  STAFF_ROLE_UPDATE_ERROR = 'staff_role_update_error',
}

export default AppEvents;
